/**
 * Shared branded report styles — matches the polished Monthly Task Report design.
 * Used by all revamped reports (MAR, Client Summary, Invoicing, etc.)
 *
 * Design tokens:
 *   - Dark headers:  #1D2939
 *   - Amber accent:  #FFAF4A / #E6A817
 *   - Label grey:    #888
 *   - Body text:     #1a1a1a
 *   - Light borders: #ccc / #ddd
 *   - Header bg:     #f0eeea
 *   - Font:          'Helvetica Neue', Helvetica, Arial, sans-serif
 */

export const BRAND = {
  dark: "#1D2939",
  amber: "#E6A817",
  amberLight: "#FFAF4A",
  labelGrey: "#888",
  bodyText: "#1a1a1a",
  lightBorder: "#ccc",
  headerBg: "#f0eeea",
  white: "#fff",
  muted: "#777",
  footerGrey: "#999",
  font: "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

/** Standard container width for PDF capture */
export const PDF_WIDTH = 1400;
export const PAGE_PAD = 56;

/* ── Shared inline style objects ─────────────────────────────── */

export const S = {
  page: {
    background: BRAND.white,
    padding: "28px 28px",
    fontFamily: BRAND.font,
    fontSize: 12,
    color: BRAND.bodyText,
    boxSizing: "border-box",
  },

  /* ── Header ── */
  pageHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingBottom: 12,
    borderBottom: `2px solid ${BRAND.bodyText}`,
    marginBottom: 14,
  },
  branding: { display: "flex", alignItems: "center", gap: 9 },
  logoCircle: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: BRAND.amber,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  logoImg: {
    width: 30,
    height: 30,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },
  facilityName: { fontSize: 13, fontWeight: 700, color: BRAND.bodyText },
  facilitySub: { fontSize: 9, color: BRAND.muted, marginTop: 1 },
  reportRight: { textAlign: "right" },
  reportTitle: { fontSize: 17, fontWeight: 700, color: BRAND.bodyText },
  reportSub: { fontSize: 10, color: BRAND.muted, marginTop: 2 },

  /* ── Meta info grid (client details) ── */
  metaBlock: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 0,
    border: `0.5px solid ${BRAND.lightBorder}`,
    marginBottom: 14,
  },
  metaCell: { padding: "6px 10px", borderRight: `0.5px solid ${BRAND.lightBorder}` },
  metaCellLast: { padding: "6px 10px" },
  metaCellBorder: {
    padding: "6px 10px",
    borderRight: `0.5px solid ${BRAND.lightBorder}`,
    borderTop: `0.5px solid ${BRAND.lightBorder}`,
  },
  metaCellBorderLast: { padding: "6px 10px", borderTop: `0.5px solid ${BRAND.lightBorder}` },
  metaLabel: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: BRAND.labelGrey,
    marginBottom: 2,
  },
  metaValue: { fontSize: 11, fontWeight: 500, color: BRAND.bodyText },

  /* ── Staff grid ── */
  staffGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 0,
    border: `0.5px solid ${BRAND.lightBorder}`,
    marginBottom: 14,
  },
  staffHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 60px 90px",
    background: BRAND.headerBg,
    borderBottom: `0.5px solid ${BRAND.lightBorder}`,
    padding: "4px 10px",
    fontSize: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: BRAND.muted,
  },
  staffRow: {
    display: "grid",
    gridTemplateColumns: "1fr 60px 90px",
    padding: "5px 10px",
    alignItems: "center",
  },
  sigBox: { border: `0.5px solid #ddd`, height: 24, background: BRAND.white },

  /* ── Section headers ── */
  sectionHeader: {
    background: BRAND.dark,
    color: BRAND.white,
    padding: "6px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 0,
  },

  /* ── Table styles ── */
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 11,
    fontFamily: BRAND.font,
  },
  th: {
    background: BRAND.headerBg,
    padding: "5px 8px",
    fontSize: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: BRAND.muted,
    borderBottom: `0.5px solid ${BRAND.lightBorder}`,
    borderRight: `0.5px solid ${BRAND.lightBorder}`,
    textAlign: "left",
  },
  td: {
    padding: "5px 8px",
    borderBottom: `0.5px solid ${BRAND.lightBorder}`,
    borderRight: `0.5px solid ${BRAND.lightBorder}`,
    fontSize: 11,
    color: BRAND.bodyText,
    verticalAlign: "top",
  },
  tdCenter: {
    padding: "4px 2px",
    borderBottom: `0.5px solid ${BRAND.lightBorder}`,
    borderRight: `0.5px solid ${BRAND.lightBorder}`,
    fontSize: 10,
    color: BRAND.bodyText,
    textAlign: "center",
    verticalAlign: "middle",
  },

  /* ── Footer ── */
  footer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    marginTop: 14,
    borderTop: `0.5px solid ${BRAND.lightBorder}`,
    fontSize: 9,
    color: BRAND.footerGrey,
  },

  /* ── Matrix wrapper ── */
  matrixWrap: {
    overflow: "visible",
    border: `0.5px solid ${BRAND.lightBorder}`,
    width: "100%",
  },
};

/* ── SVG Export Icons ── */

export const PdfIcon = ({ size = 50 }) => (
  <svg viewBox="0 0 44 50" width={size * 0.88} height={size}>
    <path
      d="M4 2C4 0.9 4.9 0 6 0H30L42 12V46C42 47.1 41.1 48 40 48H6C4.9 48 4 47.1 4 46V2Z"
      fill="#fff"
      stroke="#dc2626"
      strokeWidth="1.5"
    />
    <path
      d="M30 0L42 12H32C30.9 12 30 11.1 30 10V0Z"
      fill="#fee2e2"
      stroke="#dc2626"
      strokeWidth="1"
    />
    <line x1="12" y1="20" x2="32" y2="20" stroke="#e5e7eb" strokeWidth="1.2" />
    <line x1="12" y1="25" x2="28" y2="25" stroke="#e5e7eb" strokeWidth="1.2" />
    <line x1="12" y1="30" x2="30" y2="30" stroke="#e5e7eb" strokeWidth="1.2" />
    <rect x="2" y="34" width="26" height="14" rx="2" fill="#dc2626" />
    <text x="15" y="44" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">
      PDF
    </text>
  </svg>
);

export const XlsIcon = ({ size = 50 }) => (
  <svg viewBox="0 0 44 50" width={size * 0.88} height={size}>
    <path
      d="M4 2C4 0.9 4.9 0 6 0H30L42 12V46C42 47.1 41.1 48 40 48H6C4.9 48 4 47.1 4 46V2Z"
      fill="#fff"
      stroke="#16a34a"
      strokeWidth="1.5"
    />
    <path
      d="M30 0L42 12H32C30.9 12 30 11.1 30 10V0Z"
      fill="#dcfce7"
      stroke="#16a34a"
      strokeWidth="1"
    />
    <line x1="12" y1="20" x2="32" y2="20" stroke="#e5e7eb" strokeWidth="1.2" />
    <line x1="12" y1="25" x2="28" y2="25" stroke="#e5e7eb" strokeWidth="1.2" />
    <line x1="12" y1="30" x2="30" y2="30" stroke="#e5e7eb" strokeWidth="1.2" />
    <rect x="2" y="34" width="26" height="14" rx="2" fill="#16a34a" />
    <text x="15" y="44" textAnchor="middle" fill="#fff" fontSize="9" fontWeight="700">
      XLS
    </text>
  </svg>
);
